import { DashboardFilters } from '@delivery-metrics/shared';

export interface ShareableState {
  filters: DashboardFilters;
  viewState: {
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
    currentPage?: number;
    pageSize?: number;
    selectedRows?: string[];
    expandedSections?: string[];
    chartZoomLevel?: number;
    activeTab?: string;
    drillDownPath?: string[];
    [key: string]: any;
  };
  timestamp: number;
}

/**
 * Generate a shareable URL from current dashboard state
 */
export function generateShareableURL(
  filters: DashboardFilters,
  viewState: ShareableState['viewState'] = {},
  baseUrl: string = window.location.origin + window.location.pathname
): string {
  const state: ShareableState = {
    filters,
    viewState,
    timestamp: Date.now(),
  };

  const encodedState = encodeURIComponent(JSON.stringify(state));
  return `${baseUrl}?state=${encodedState}`;
}

/**
 * Parse a shareable URL to extract dashboard state
 */
export function parseShareableURL(url: string): ShareableState | null {
  try {
    const urlObj = new URL(url);
    const stateParam = urlObj.searchParams.get('state');

    if (!stateParam) {
      return null;
    }

    const state = JSON.parse(decodeURIComponent(stateParam)) as ShareableState;

    // Validate required fields
    if (!state.filters || !state.filters.timeRange) {
      return null;
    }

    // Convert date strings back to Date objects
    if (typeof state.filters.timeRange.start === 'string') {
      state.filters.timeRange.start = new Date(state.filters.timeRange.start);
    }
    if (typeof state.filters.timeRange.end === 'string') {
      state.filters.timeRange.end = new Date(state.filters.timeRange.end);
    }

    return state;
  } catch (error) {
    console.warn('Failed to parse shareable URL:', error);
    return null;
  }
}

/**
 * Get current URL search parameters as an object
 */
export function getCurrentUrlState(): ShareableState | null {
  return parseShareableURL(window.location.href);
}

/**
 * Update the current URL with new state without triggering navigation
 */
export function updateUrlState(
  filters: DashboardFilters,
  viewState: ShareableState['viewState'] = {}
): void {
  const newUrl = generateShareableURL(filters, viewState);
  const urlObj = new URL(newUrl);

  // Only update the search params, keep the rest of the URL
  const newSearchParams = urlObj.searchParams.toString();
  const newPath = `${window.location.pathname}?${newSearchParams}`;

  window.history.replaceState(null, '', newPath);
}

/**
 * Copy shareable URL to clipboard
 */
export async function copyShareableURL(
  filters: DashboardFilters,
  viewState: ShareableState['viewState'] = {}
): Promise<boolean> {
  try {
    const url = generateShareableURL(filters, viewState);
    await navigator.clipboard.writeText(url);
    return true;
  } catch (error) {
    console.error('Failed to copy URL to clipboard:', error);
    return false;
  }
}
